import {
  getAllowedGroupIds,
  getGroupIdOptionsFromDetailsAPIResponse,
} from "./group-id/groupIdHelpers";
import { getDepartmentsByRoles } from "../../../api";
import {
  RowDataValueType,
  IAMUserState,
  ISelectableOption,
  PrimitiveValueType,
  RowDataObject,
  GroupCheckboxSelectedOptions,
  IOption,
  StoreState,
  LandingPageOptions,
  RowData,
  EvaluatedResults,
  ObjectTypes,
  ToastInfo,
  RowDataReducerState,
  SetupListNavInfoByDepartment,
  EnumLabels,
  ExtendedAccountInfo,
} from "../../../types";
import {
  SETUPS_TO_HIDE,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
  MISMATCHED_FUNCTIONAL_UNIT_NAMES,
  NO_RESULTS_DEFAULT_IOPTION,
  EVALUATED_RESULT_KEYS,
} from "../constants";
import {
  getChartOfAccountsSubTypeOptions,
  getFilteredCashAccountOptions,
  getFilteredCashClearingAccountOptions,
  getFilteredInterCoTransferCompanyBStore,
  getFilteredRemittanceFormMappingCashAccountOptions,
  getInterCoTransferTargetCompanyGroupIdOptions,
  getPOConfigurationGroupGLStoreOptions,
} from "./customRowDataHelpers";
import {
  getCashClearingIntegratingApplicationOptions,
  getEvaluatedPOAuthoritiesOptions,
  getInventoryBuildingsRouteOptions,
  getRemittanceFormMappingFormIdOptions,
  getRemittanceFormMappingProvisionIdOptions,
} from "./evaluationHelpers";
import { getStoreLevelAccountName } from "./sharedHelpers";
import { getStoreOptionsFromDetailsAPIResponse } from "./storeHelpers";

/**
 * @deprecated Use `getStoreOptionsFromDetailsAPIResponse` instead.
 * @param stores
 * @returns
 */
export function getStoreOptions(stores: Array<StoreState>): Array<IOption> {
  const storeOptions: Array<IOption> = stores.map((store: StoreState) => ({
    label: `${store.id} - ${store.name}`,
    value: store.id,
    disabled: false,
  }));
  storeOptions.sort((a: IOption, b: IOption) => a.label.localeCompare(b.label));
  return storeOptions;
}

export function getStoreOptionsFromExtendedAccountInfo(
  extendedAccountInfo: ExtendedAccountInfo[],
): IOption[] {
  const storeOptions = extendedAccountInfo.map((accountInfo) => ({
    label: `${accountInfo.storeId} - ${accountInfo.storeName}`,
    value: accountInfo.storeId,
    disabled: false,
  }));

  storeOptions.sort((a, b) => a.label.localeCompare(b.label));

  return storeOptions;
}

export async function getDepartmentOptions(
  userRoles: string,
): Promise<{ options: Array<IOption>; toastInfo?: ToastInfo }> {
  const response = await getDepartmentsByRoles(userRoles);
  const hasError = !response.resultCode.includes("200");

  if (hasError) {
    return {
      options: [NO_RESULTS_DEFAULT_IOPTION()],
      toastInfo: {
        variant: "negative",
        message: response.resultObj as string,
      },
    };
  }

  const departmentMap = response.resultObj as ObjectTypes;

  const options: Array<IOption> = Object.keys(departmentMap)
    .map((key) => ({
      label: key,
      value: departmentMap[key],
      disabled: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { options };
}

export function getSetupOptions(
  department: string,
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment,
): { options: Array<IOption>; toastInfo?: ToastInfo } {
  const setups = setupListNavInfoByDepartment?.[department];

  if (!setups) {
    return {
      options: [NO_RESULTS_DEFAULT_IOPTION()],
    };
  }

  const options: Array<IOption> = setups.setups
    .map((setup) => ({
      label: setup.text,
      value: setup.id,
      disabled: setup.isDisabled ?? false,
    }))
    .filter((setup) => !SETUPS_TO_HIDE.includes(setup.value))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { options };
}

export const getSelectableOptions = ({
  propertyKey,
  enumValues,
  enumLabels,
  iamUserState,
  selected,
  isStore,
  isGroupId,
  selectedTabSectionKey,
  evaluatedResults,
  rowData,
  rowDataList,
  rowIndex,
  setupListNavInfoByDepartment,
}: {
  propertyKey: string;
  enumValues: PrimitiveValueType[];
  enumLabels?: EnumLabels;
  iamUserState: IAMUserState;
  selected: LandingPageOptions;
  isStore: boolean | undefined;
  isGroupId: boolean | undefined;
  selectedTabSectionKey: string | undefined;
  evaluatedResults?: EvaluatedResults;
  rowData: RowData;
  rowDataList?: RowData[];
  rowIndex?: number;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
}): ISelectableOption[] => {
  const { store, department, setup } = selected;
  const fixedSetupKey =
    MISMATCHED_FUNCTIONAL_UNIT_NAMES.get(setup.value) ?? setup.value;

  const allowedGroupIds = getAllowedGroupIds({
    selected,
    setupListNavInfoByDepartment,
    iamUserState,
  });

  const resolvedOptions = resolveDropdownOptions({
    enumValues,
    iamUserState,
    isStore,
    isGroupId,
    enumLabels,
    allowedGroupIds,
  });

  const storeLevelAccountOptions = convertToOptions([
    getStoreLevelAccountName(
      store.value,
      department.value,
      iamUserState.extendedAccountInfo,
    ),
  ]);

  const assignGroupAccounts = setupListNavInfoByDepartment
    ? Object.values(setupListNavInfoByDepartment)
        .flatMap((item) => item.departmentIds)
        .filter((dept) => !dept.name.includes("-A"))
        .map((dept) => dept.name)
    : [];

  const fieldOptionsToUpdate = [
    {
      setup: SCHEMA_TITLES.PO_CONFIGURATION_GROUP,
      fields: [ROW_DATA_KEYS.GROUP_ACCOUNTS],
      options: convertToOptions(assignGroupAccounts),
    },
    {
      setup: SCHEMA_TITLES.PO_CONFIGURATION_GROUP,
      fields: [ROW_DATA_KEYS.REF_STORE],
      options: getPOConfigurationGroupGLStoreOptions(
        selectedTabSectionKey,
        iamUserState.extendedAccountInfo,
        rowData,
      ),
    },
    {
      setup: SCHEMA_TITLES.PO_ROLE_LEVEL_INFO,
      fields: [ROW_DATA_KEYS.ROLE_LEVEL],
      options: getEvaluatedPOAuthoritiesOptions(evaluatedResults),
    },
    {
      setup: SCHEMA_TITLES.CHART_OF_ACCOUNTS,
      fields: [ROW_DATA_KEYS.SUB_TYPE],
      options: getChartOfAccountsSubTypeOptions(
        enumValues,
        rowData,
        enumLabels,
      ),
    },
    {
      setup: SCHEMA_TITLES.AR_PORTAL,
      fields: [ROW_DATA_KEYS.ACCOUNTING_ACCOUNT],
      options: storeLevelAccountOptions,
    },
    {
      setup: SCHEMA_TITLES.REMITTANCE_FORM_MAPPING,
      fields: [ROW_DATA_KEYS.FORM_ID],
      options: getRemittanceFormMappingFormIdOptions(evaluatedResults, rowData),
    },
    {
      setup: SCHEMA_TITLES.REMITTANCE_FORM_MAPPING,
      fields: [ROW_DATA_KEYS.PROVISION_ID],
      options: getRemittanceFormMappingProvisionIdOptions(
        evaluatedResults,
        rowData,
        rowIndex,
      ),
    },
    {
      setup: SCHEMA_TITLES.INVENTORY_BUILDINGS,
      fields: [ROW_DATA_KEYS.ROUTE_NAMES],
      options: getInventoryBuildingsRouteOptions(evaluatedResults),
    },
    {
      setup: SCHEMA_TITLES.DEFAULT_LABOR_TYPES,
      fields: [ROW_DATA_KEYS.WRITEUP_TYPES], // TODO: needs to be updated to correct key when available (DEFAULT_LABOR_TYPE)
      options:
        (evaluatedResults?.[
          EVALUATED_RESULT_KEYS.DEFAULT_LABOR_TYPE_OPTIONS
        ] as ISelectableOption[] | undefined) ?? [],
    },
    {
      setup: SCHEMA_TITLES.DEFAULT_LABOR_TYPES,
      fields: [ROW_DATA_KEYS.CUSTOMER_PAY_TYPE],
      options:
        (evaluatedResults?.[EVALUATED_RESULT_KEYS.CUSTOMER_PAY_TYPE_OPTIONS] as
          | ISelectableOption[]
          | undefined) ?? [],
    },
    {
      setup: SCHEMA_TITLES.DEFAULT_LABOR_TYPES,
      fields: [ROW_DATA_KEYS.WARRANTY_PAY_TYPE],
      options:
        (evaluatedResults?.[EVALUATED_RESULT_KEYS.WARRANTY_PAY_TYPE_OPTIONS] as
          | ISelectableOption[]
          | undefined) ?? [],
    },
    {
      setup: SCHEMA_TITLES.DEFAULT_LABOR_TYPES,
      fields: [ROW_DATA_KEYS.INTERNAL_PAY_TYPE],
      options:
        (evaluatedResults?.[EVALUATED_RESULT_KEYS.INTERNAL_PAY_TYPE_OPTIONS] as
          | ISelectableOption[]
          | undefined) ?? [],
    },
    {
      setup: SCHEMA_TITLES.CASH_CLEARING_SETUP,
      fields: [
        ROW_DATA_KEYS.CASH_IN_BANK_COMPANY_GROUP_ID,
        ROW_DATA_KEYS.CASH_CLEARING_COMPANY_GROUP_ID,
      ],
      options:
        (evaluatedResults?.[
          EVALUATED_RESULT_KEYS.INTERCO_COMPANY_GROUP_ID_OPTIONS
        ] as ISelectableOption[] | undefined) ?? [],
    },
    {
      setup: SCHEMA_TITLES.CASH_CLEARING_SETUP,
      fields: [ROW_DATA_KEYS.INTEGRATING_APPLICATION],
      options: getCashClearingIntegratingApplicationOptions({
        rowData,
        options: resolvedOptions,
        rowDataList: rowDataList ?? [],
      }),
    },
    {
      setup: SCHEMA_TITLES.INTERCO_TRANSFER,
      fields: [ROW_DATA_KEYS.TARGET_COMPANY_GROUP_ID],
      options: getInterCoTransferTargetCompanyGroupIdOptions({
        rowData,
        options: resolvedOptions,
        rowDataList: rowDataList ?? [],
      }),
    },
  ];

  const matchingField = fieldOptionsToUpdate.find(
    (item) => item.setup === fixedSetupKey && item.fields.includes(propertyKey),
  );

  if (matchingField) {
    return matchingField.options;
  }

  return resolvedOptions;
};

export const getSelectableTypeAheadOptions = ({
  propertyKey,
  options,
  selected,
  rowDataReducerState,
}: {
  propertyKey: string;
  options: ISelectableOption[];
  selected: LandingPageOptions;
  rowDataReducerState: RowDataReducerState;
}): ISelectableOption[] => {
  const { rowData, rowDataList } = rowDataReducerState;
  const { setup } = selected;

  const fixedSetupKey =
    MISMATCHED_FUNCTIONAL_UNIT_NAMES.get(setup.value) ?? setup.value;

  const fieldOptionsToUpdate = [
    {
      setup: SCHEMA_TITLES.CASH_APP_DEFAULTS,
      fields: [ROW_DATA_KEYS.CASH_ACCOUNT],
      options: getFilteredCashAccountOptions(options, rowData),
    },
    {
      setup: SCHEMA_TITLES.CASH_APP_DEFAULTS,
      fields: [ROW_DATA_KEYS.CASH_CLEARING_ACCOUNT],
      options: getFilteredCashClearingAccountOptions(options, rowData),
    },
    {
      setup: SCHEMA_TITLES.INTERCO_TRANSFER,
      fields: [ROW_DATA_KEYS.COMPANY_B_STORE],
      options: getFilteredInterCoTransferCompanyBStore(options, rowData),
    },
    {
      setup: SCHEMA_TITLES.REMITTANCE_FORM_MAPPING,
      fields: [ROW_DATA_KEYS.CASH_ACCOUNT],
      options: getFilteredRemittanceFormMappingCashAccountOptions(
        options,
        rowDataList,
      ),
    },
  ];

  const matchingField = fieldOptionsToUpdate.find(
    (item) => item.setup === fixedSetupKey && item.fields.includes(propertyKey),
  );

  if (matchingField) {
    return matchingField.options;
  }

  return options;
};

export const getSelectedOption = ({
  inputValue,
  optionsList,
}: {
  inputValue: RowDataValueType;
  optionsList: ISelectableOption[];
}): ISelectableOption => {
  return (
    optionsList.find(
      (option) => option.value === inputValue && !option.disabled,
    ) ?? {
      label: "",
      value: "",
    }
  );
};

export const resolveDropdownOptions = ({
  enumValues,
  enumLabels,
  iamUserState,
  isStore,
  isGroupId,
  allowedGroupIds,
}: {
  enumValues: PrimitiveValueType[];
  enumLabels?: EnumLabels;
  iamUserState: IAMUserState;
  isStore: boolean | undefined;
  isGroupId: boolean | undefined;
  allowedGroupIds: string[];
}): ISelectableOption[] => {
  if (isGroupId) {
    return getGroupIdOptionsFromDetailsAPIResponse(
      iamUserState.eisGroups.groups,
      allowedGroupIds,
    );
  }
  if (isStore) {
    return getStoreOptionsFromDetailsAPIResponse(
      iamUserState.extendedAccountInfo,
    );
  }
  return convertToOptions(enumValues, enumLabels);
};

/** @deprecated Use resolveDropdownOptions instead */
export const getEnumOrStoreOrGroupOptions = (
  enumValues: PrimitiveValueType[],
  iamUserState: IAMUserState,
  isStore: boolean | undefined,
  groupId: string | undefined,
): ISelectableOption[] =>
  resolveDropdownOptions({
    enumValues,
    iamUserState,
    isStore,
    isGroupId: groupId !== undefined,
    allowedGroupIds: [],
  });

export const getSelectedOptions = (
  inputValue: RowDataValueType,
  valueKey?: string,
  enumLabels?: EnumLabels,
): ISelectableOption[] => {
  if (Array.isArray(inputValue)) {
    if (inputValue[0] && typeof inputValue[0] === "object") {
      return convertGenericObjectToOptions(
        inputValue as RowDataObject[],
        valueKey,
        enumLabels,
      );
    }
    return convertToOptions(inputValue as Array<PrimitiveValueType>);
  }
  return [];
};

export const convertToOption = (
  inputValue: PrimitiveValueType,
  enumLabels?: EnumLabels,
): ISelectableOption => {
  const overrideLabel = enumLabels?.[String(inputValue)] ?? undefined;

  if (typeof inputValue === "string" || typeof inputValue === "boolean") {
    return { label: overrideLabel ?? String(inputValue), value: inputValue };
  } else if (typeof inputValue === "number") {
    return { label: overrideLabel ?? `${inputValue}`, value: inputValue };
  } else {
    return { label: overrideLabel ?? "None", value: null };
  }
};

export const convertToOptions = (
  inputValue: PrimitiveValueType[],
  enumLabels?: EnumLabels,
): ISelectableOption[] => {
  if (!inputValue) return [];
  return inputValue.map((value) => convertToOption(value, enumLabels));
};

export const convertGenericObjectToOptions = (
  inputValue: RowDataObject[],
  valueKey?: string,
  enumLabels?: EnumLabels,
): ISelectableOption[] => {
  if (valueKey) {
    return inputValue
      .map((item) => item[valueKey] as PrimitiveValueType)
      .filter((value) => value !== undefined)
      .map((value) => {
        const label =
          enumLabels?.[String(value)] ?? (value === null ? "None" : `${value}`);
        return { label, value };
      });
  }

  const options: ISelectableOption[] = [];
  const excludedKeys = [
    ROW_DATA_KEYS.ID,
    ROW_DATA_KEYS.DMS_GROUP_BUS_UNIT_ID,
    ROW_DATA_KEYS.ACTIVE_STATUS,
  ];
  inputValue.forEach((item) => {
    for (const [key, value] of Object.entries(item)) {
      if (!excludedKeys.includes(key)) {
        const label = value === null ? "None" : `${value}`;
        options.push({ label, value: value as PrimitiveValueType });
      }
    }
  });

  return options;
};

export const convertToGroupCheckBoxSelectedOptions = (
  options: ISelectableOption[],
): GroupCheckboxSelectedOptions => {
  const result: GroupCheckboxSelectedOptions = {};
  options.forEach((item) => {
    if (typeof item.value === "string" || typeof item.value === "number") {
      result[item.value] = true;
    }
  });
  return result;
};
